/**
 * Shared Contact form used by:
 * - Contact us section (landing page, logged-out users)
 * - Contact Support modal (logged-in users)
 * Ensures identical validation, layout, and UX for both.
 */
import { useRef } from 'react'
import { useLanguage } from '../contexts/LanguageContext'

export function ContactForm({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  message,
  setMessage,
  messageCharLimit = 1000,
  location: locationVal = '',
  setLocation: setLocationVal,
  phone = '',
  setPhone,
  company = '',
  setCompany,
  links: linksVal = '',
  setLinks: setLinksVal,
  attachments = [],
  onAddAttachments,
  onRemoveAttachment,
  fieldErrors,
  showFieldErrors = false,
  status,
  statusType,
  onFieldChange,
  onSubmit,
  submitLabel,
  messagePlaceholder,
  attachmentsHint,
  className = '',
  /** Shorter fields for modal / tight layouts (landing contact unchanged) */
  compact = false,
}) {
  const { t } = useLanguage()
  const fileInputRef = useRef(null)
  const submitLabelResolved = submitLabel ?? t('contact.sendMessage')
  const messagePlaceholderResolved = messagePlaceholder ?? t('contact.placeholderMessage')
  const attachmentsHintResolved = attachmentsHint ?? t('contact.attachmentsHint')
  const messageRows = compact ? 3 : 5
  const linksRows = compact ? 2 : 3

  const handleChange = (setter) => (e) => {
    setter(e.target.value)
    onFieldChange?.()
  }

  const handleMessageChange = (e) => {
    const val = e.target.value
    if (val.length <= messageCharLimit) {
      setMessage(val)
    } else {
      setMessage(val.slice(0, messageCharLimit))
    }
    onFieldChange?.()
  }

  const handlePaste = (e) => {
    const pasted = (e.clipboardData?.getData('text') || '')
    const combined = message + pasted
    if (combined.length > messageCharLimit) {
      e.preventDefault()
      setMessage(combined.slice(0, messageCharLimit))
    }
  }

  const handleAddAttachments = (e) => {
    const files = e.target.files
    if (files?.length && onAddAttachments) {
      onAddAttachments(Array.from(files))
      onFieldChange?.()
    }
    e.target.value = ''
  }

  const handleRemoveAttachment = (i) => {
    onRemoveAttachment?.(i)
    onFieldChange?.()
  }

  const totalAttachmentBytes = attachments.reduce((s, a) => s + (a.file?.size || 0), 0)
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`
  }

  return (
    <div className={className}>
      <div className="name-row">
        <label className={`field ${showFieldErrors && fieldErrors.firstName ? 'field--error' : ''}`}>
          <span>{t('contact.firstName')} <span className="field-required" aria-hidden="true">{t('contact.required')}</span></span>
          {showFieldErrors && fieldErrors.firstName && (
            <span className="field-error">{fieldErrors.firstName}</span>
          )}
          <input
            type="text"
            placeholder={t('contact.placeholderFirst')}
            value={firstName}
            onChange={handleChange(setFirstName)}
          />
        </label>
        <label className={`field ${showFieldErrors && fieldErrors.lastName ? 'field--error' : ''}`}>
          <span>{t('contact.lastName')} <span className="field-required" aria-hidden="true">{t('contact.required')}</span></span>
          {showFieldErrors && fieldErrors.lastName && (
            <span className="field-error">{fieldErrors.lastName}</span>
          )}
          <input
            type="text"
            placeholder={t('contact.placeholderLast')}
            value={lastName}
            onChange={handleChange(setLastName)}
          />
        </label>
      </div>
      <label className={`field ${showFieldErrors && fieldErrors.email ? 'field--error' : ''}`}>
        <span>{t('contact.email')} <span className="field-required" aria-hidden="true">{t('contact.required')}</span></span>
        {showFieldErrors && fieldErrors.email && (
          <span className="field-error">{fieldErrors.email}</span>
        )}
        <input
          type="email"
          placeholder={t('contact.placeholderEmail')}
          value={email}
          onChange={handleChange(setEmail)}
        />
      </label>
      <div className="contact-extra-row">
        <label className="field">
          <span>{t('contact.location')}</span>
          <input
            type="text"
            placeholder={t('contact.locationPlaceholder')}
            value={locationVal}
            onChange={setLocationVal ? handleChange(setLocationVal) : () => {}}
          />
        </label>
        <label className="field">
          <span>{t('contact.phone')}</span>
          <input
            type="tel"
            placeholder={t('contact.phonePlaceholder')}
            value={phone}
            onChange={setPhone ? handleChange(setPhone) : () => {}}
          />
        </label>
      </div>
      <label className="field">
        <span>{t('contact.company')}</span>
        <input
          type="text"
          placeholder={t('contact.companyPlaceholder')}
          value={company}
          onChange={setCompany ? handleChange(setCompany) : () => {}}
        />
      </label>
      <label className={`field ${showFieldErrors && fieldErrors.message ? 'field--error' : ''}`}>
        <span>{t('contact.message')} <span className="field-required" aria-hidden="true">{t('contact.required')}</span></span>
        {showFieldErrors && fieldErrors.message && (
          <span className="field-error">{fieldErrors.message}</span>
        )}
        <textarea
          placeholder={messagePlaceholderResolved}
          rows={messageRows}
          value={message}
          onChange={handleMessageChange}
          onPaste={handlePaste}
          maxLength={messageCharLimit}
        />
        <span className={`contact-char-count ${message.length >= messageCharLimit ? 'contact-char-count--limit' : ''}`}>
          {message.length}/{messageCharLimit}
        </span>
      </label>
      <label className="field contact-links-field">
        <span>{t('contact.links')}</span>
        <textarea
          placeholder={t('contact.linksPlaceholder')}
          rows={linksRows}
          value={linksVal}
          onChange={setLinksVal ? handleChange(setLinksVal) : () => {}}
          className="contact-links-input"
        />
      </label>
      <div className={`contact-attachments ${showFieldErrors && fieldErrors.attachments ? 'contact-attachments--error' : ''}`}>
        <div className="contact-attachments-header">
          <span>{t('contact.attachments')}</span>
          <span className="contact-attachments-hint">{attachmentsHintResolved}</span>
        </div>
        {showFieldErrors && fieldErrors.attachments && (
          <span className="field-error">{fieldErrors.attachments}</span>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="contact-attachments-input"
          onChange={handleAddAttachments}
          aria-label={t('contact.addAttachmentsAria')}
        />
        <button
          type="button"
          className="ghost-button contact-attachments-add"
          onClick={() => fileInputRef.current?.click()}
        >
          {t('contact.addFiles')}
        </button>
        {attachments.length > 0 && (
          <ul className="contact-attachments-list">
            {attachments.map((item, i) => (
              <li key={i} className="contact-attachment-item">
                <span className="contact-attachment-name">{item.file?.name || 'File'}</span>
                <span className="contact-attachment-size">{formatSize(item.file?.size || 0)}</span>
                <button
                  type="button"
                  className="contact-attachment-remove"
                  onClick={() => handleRemoveAttachment(i)}
                  aria-label={`Remove ${item.file?.name}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        {attachments.length > 0 && (
          <span className="contact-attachments-total">
            Total: {formatSize(totalAttachmentBytes)} / 2 MB
          </span>
        )}
      </div>
      {status && (
        <p
          className={`status-note ${statusType === 'success' ? 'status-success status-success--pill' : statusType === 'error' ? 'status-error' : ''}`}
          role={statusType === 'success' || statusType === 'error' ? 'status' : undefined}
        >
          {statusType === 'success' ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : null}
          <span>{status}</span>
        </p>
      )}
      <div className="contact-submit-wrap">
        <button
          type="button"
          className="primary-button"
          onClick={onSubmit}
        >
          {submitLabelResolved}
        </button>
      </div>
    </div>
  )
}
